import { Injectable, signal, computed } from '@angular/core';
import { CallState, CallSession } from './call-types';

/**
 * Centralized call state store using Angular signals
 * Single source of truth for call state across the application
 *
 * Based on CallStateSimpleService pattern - clean, reactive, and performant
 */
@Injectable({
  providedIn: 'root'
})
export class CallStateStore {
  // Private signals (internal state)
  private _callState = signal<CallState>('idle');
  private _isConnected = signal<boolean>(false);
  private _callDuration = signal<number>(0);
  private _activeCall = signal<CallSession | null>(null);
  private _isProcessing = signal<boolean>(false);
  private _lastError = signal<string | null>(null);

  // Public readonly signals (read-only access)
  readonly callState = this._callState.asReadonly();
  readonly isConnected = this._isConnected.asReadonly();
  readonly callDuration = this._callDuration.asReadonly();
  readonly activeCall = this._activeCall.asReadonly();
  readonly isProcessing = this._isProcessing.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  // Computed signals (derived state)
  readonly canMakeCall = computed(() =>
    this._isConnected() &&
    this._callState() === 'idle' &&
    !this._isProcessing()
  );

  readonly isInCall = computed(() =>
    ['dialing', 'ringing', 'connecting', 'connected', 'held'].includes(this._callState())
  );

  readonly isCallActive = computed(() =>
    ['dialing', 'ringing', 'connecting', 'connected', 'held'].includes(this._callState())
  );

  readonly canEndCall = computed(() =>
    this.isCallActive() && !this._isProcessing()
  );

  readonly isTerminalState = computed(() =>
    ['ended', 'failed', 'post-call', 'idle'].includes(this._callState())
  );

  /**
   * Update call state
   * Package-private - only call services should use this
   */
  setState(state: CallState): void {
    this._callState.set(state);
  }

  /**
   * Update connection status
   */
  setConnected(connected: boolean): void {
    this._isConnected.set(connected);
  }

  /**
   * Update call duration
   */
  setDuration(duration: number): void {
    this._callDuration.set(duration);
  }

  /**
   * Update active call session
   */
  setActiveCall(call: CallSession | null): void {
    this._activeCall.set(call);
  }

  /**
   * Update processing state
   */
  setProcessing(processing: boolean): void {
    this._isProcessing.set(processing);
  }

  /**
   * Set error message
   */
  setError(error: string | null): void {
    this._lastError.set(error);
  }

  /**
   * Reset all state to idle
   */
  reset(): void {
    this._callState.set('idle');
    this._callDuration.set(0);
    this._activeCall.set(null);
    this._isProcessing.set(false);
    this._lastError.set(null);
  }

  /**
   * Get current state synchronously (for conditions/validation)
   */
  getCurrentState(): CallState {
    return this._callState();
  }

  /**
   * Get active call synchronously
   */
  getActiveCall(): CallSession | null {
    return this._activeCall();
  }
}
